import React from 'react';

/**
 * PageErrorBoundary - evita "tela branca" em páginas inteiras.
 *
 * Quando um componente filho lança erro em render, mostra uma mensagem
 * amigável com o erro (para o usuário reportar) e botões de recuperação.
 */
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      console.error('[PageErrorBoundary]', error, errorInfo);
    } catch {
      /* noop */
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  goBack = () => {
    try { window.history.back(); } catch { /* noop */ }
  };

  reload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const pageName = this.props.pageName || 'nesta página';
    const errorMessage = (this.state.error && this.state.error.message) || 'Erro desconhecido';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-testid="page-error-boundary">
        <div className="max-w-xl w-full bg-white rounded-lg shadow-sm border border-amber-200 p-6">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-xl">
              !
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Algo deu errado {pageName}
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                Encontramos um problema ao renderizar esta página. Seus dados salvos não foram afetados.
              </p>
              <details className="mb-4 text-xs text-gray-500 bg-gray-50 rounded p-2 border">
                <summary className="cursor-pointer text-gray-700 font-medium">Detalhes técnicos</summary>
                <div className="mt-2 font-mono break-words whitespace-pre-wrap">
                  {errorMessage}
                </div>
              </details>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={this.goBack}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={this.reload}
                  className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Recarregar página
                </button>
                <button
                  type="button"
                  onClick={this.reset}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
